// The 3D snooker table, built in code (replaces Models/Snooker/mesa.obj). Rendering only: the physics is Table.ts.
//
// Every outline comes from tableShapes.ts, which takes it from the physics (tableShapes.test.ts checks this):
// the cushion noses and pocket liners follow the physics walls, the dark pocket openings are the pocket polygons,
// and the markings come from shared/rules.ts. Parts, from the playing surface out:
//   cloth bed (textured, with the pocket openings cut out) and its markings: baulk line, D and the six spots
//   cushions: a rounded rubber profile swept along the cushion line, cloth on the cushions and jaws, blending into
//     leather round the backs of the pockets, where the liner turns down into the pocket
//   flat cushion tops (cloth, leather behind the pockets), the dark pocket openings (wall and bottom)
//   wooden rails (wood-grain texture, grain along each rail, mitred at the corners) with diamond markers,
//     a rounded outer edge, the frame (apron) below it and six turned legs.
// Textures: textures/table/{cloth,wood,leather}.jpg (client/scripts/gen-table-textures.py). Materials are Phong/Lambert
// lit by the scene's existing ambient + spot light. Nothing casts a shadow map: the balls' shadows are soft blobs on
// the cloth (ballShadows.ts).
//
// Detail follows the graphics quality (quality.ts): on High the curved parts of the cushion line (jaws, pocket backs)
// are drawn smoothed through the physics points (smoothCushionPath), so the liners and the pocket openings, which
// share that line's points, are rounder, and the rail corners and legs have more segments; Low is the plain physics outline with fewer segments, and both use
// textures at the profile's resolution.
import {BAULK_Y, D_CENTRE, D_RADIUS, SPOTS, TABLE, Vec} from "./shared/rules";
import {
    BED_Y, CUSHION_TOP_Y, FRAME_BOTTOM_Y, NOSE_WIDTH, NOSE_Y, POCKET_DEPTH, RAIL_BOTTOM_Y, RAIL_TOP_Y,
    densify, diamondPositions, linerWeight, outwardMitres, pocketHole, railInnerEdge, railOuterEdge,
    signedArea, smoothCushionPath, TABLE_CENTRE,
} from "./tableShapes";
import {POCKETS} from "./tableGeometry";
import {QUALITY_PROFILES, QualityProfile} from "./quality";
import {imageTexture} from "./textures";
import earcut from "earcut";

declare const THREE: any;

const CLOTH_TILE = 2.6;   // texture repeat on the cloth (scene units per tile)
const WOOD_TILE_U = 4.0;  // along the grain
const WOOD_TILE_V = 2.0;

type UV = [number, number];

/** Collects triangles with per-face material indices and UVs into one THREE.Geometry. */
class MeshBuilder {
    geometry = new THREE.Geometry();
    vertex(x: number, h: number, y: number) {
        this.geometry.vertices.push(new THREE.Vector3(x, h, y));
        return this.geometry.vertices.length - 1;
    }
    tri(a: number, b: number, c: number, uv: [UV, UV, UV], material: number) {
        const g = this.geometry;
        g.faces.push(new THREE.Face3(a, b, c, undefined, undefined, material));
        g.faceVertexUvs[0].push(uv.map(t => new THREE.Vector2(t[0], t[1])));
    }
    /** Triangle facing up (+y) whatever order the points come in. */
    triUp(a: number, b: number, c: number, uv: [UV, UV, UV], material: number) {
        const v = this.geometry.vertices;
        const ny = (v[b].z - v[a].z) * (v[c].x - v[a].x) - (v[b].x - v[a].x) * (v[c].z - v[a].z);
        if (ny >= 0) this.tri(a, b, c, uv, material);
        else this.tri(a, c, b, [uv[0], uv[2], uv[1]], material);
    }
    build(smooth: boolean) {
        const g = this.geometry;
        if (g.computeCentroids) g.computeCentroids();
        g.computeFaceNormals();
        if (smooth) g.computeVertexNormals();
        else for (const f of g.faces) f.vertexNormals = [f.normal.clone(), f.normal.clone(), f.normal.clone()];
        return g;
    }
}

/**
 * A flat horizontal region at height h: `outline` minus `holes`, triangulated with earcut (three.js r58's
 * triangulator gave up on some of these outlines and silently left triangles out). uvOf gives each corner's UV and
 * materialOf each triangle's material (from its centre).
 */
function addFlat(b: MeshBuilder, outline: Vec[], holes: Vec[][], h: number,
                 uvOf: (p: Vec, centre: Vec) => UV, materialOf: (centre: Vec) => number) {
    const all: Vec[] = outline.concat(...holes);
    const holeStarts: number[] = [];
    let at = outline.length;
    for (const hole of holes) { holeStarts.push(at); at += hole.length; }
    const flat: number[] = [];
    for (const p of all) flat.push(p[0], p[1]);
    const tris = earcut(flat, holeStarts.length ? holeStarts : undefined, 2);
    const base = b.geometry.vertices.length;
    for (const p of all) b.vertex(p[0], h, p[1]);
    for (let t = 0; t < tris.length; t += 3) {
        const f = [tris[t], tris[t + 1], tris[t + 2]];
        const pts = f.map(i => all[i]);
        const c: Vec = [(pts[0][0] + pts[1][0] + pts[2][0]) / 3, (pts[0][1] + pts[1][1] + pts[2][1]) / 3];
        b.triUp(base + f[0], base + f[1], base + f[2], [uvOf(pts[0], c), uvOf(pts[1], c), uvOf(pts[2], c)], materialOf(c));
    }
}

/**
 * Sweeps a cross-section along a closed path. profile(i) gives, for path point i, the section as [offset, height]
 * pairs (offset along that point's outward mitre); all sections have the same number of points. Faces point to the
 * side given by `facing` (+1: away from the inside of the path, -1: towards it).
 */
function addSweep(b: MeshBuilder, path: Vec[], mitres: Vec[], profile: (i: number) => [number, number][],
                  facing: 1 | -1, uvScale: [number, number], materialOf: (i: number, k: number) => number) {
    const n = path.length;
    const sections = path.map((_, i) => profile(i));
    const m = sections[0].length;
    const base = b.geometry.vertices.length;
    for (let i = 0; i < n; i++) {
        for (const [d, h] of sections[i]) b.vertex(path[i][0] + mitres[i][0] * d, h, path[i][1] + mitres[i][1] * d);
    }
    // u: length along the path, v: length along the section
    const u: number[] = [0];
    for (let i = 1; i <= n; i++) u.push(u[i - 1] + Math.hypot(path[i % n][0] - path[i - 1][0], path[i % n][1] - path[i - 1][1]));
    const vOf = (i: number) => {
        const s = sections[i], out = [0];
        for (let k = 1; k < m; k++) out.push(out[k - 1] + Math.hypot(s[k][0] - s[k - 1][0], s[k][1] - s[k - 1][1]));
        return out;
    };
    // Wind the faces so they look to the requested side: measure the side the plain winding faces, summed over the
    // sweep (horizontal parts of the section don't count), and flip everything if it is the wrong one.
    const verts = b.geometry.vertices;
    let side = 0;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        for (let k = 0; k < m - 1; k++) {
            const A = verts[base + i * m + k], B = verts[base + j * m + k], C = verts[base + j * m + k + 1];
            const e1x = B.x - A.x, e1y = B.y - A.y, e1z = B.z - A.z, e2x = C.x - A.x, e2y = C.y - A.y, e2z = C.z - A.z;
            const nx = e1y * e2z - e1z * e2y, nz = e1x * e2y - e1y * e2x;
            side += (nx * mitres[i][0] + nz * mitres[i][1]) * facing;
        }
    }
    const flip = side < 0;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const vi = vOf(i), vj = vOf(j);
        for (let k = 0; k < m - 1; k++) {
            const a = base + i * m + k, bb = base + j * m + k, c = base + j * m + k + 1, d = base + i * m + k + 1;
            const uv: UV[] = [[u[i] / uvScale[0], vi[k] / uvScale[1]], [u[i + 1] / uvScale[0], vj[k] / uvScale[1]],
                [u[i + 1] / uvScale[0], vj[k + 1] / uvScale[1]], [u[i] / uvScale[0], vi[k + 1] / uvScale[1]]];
            const mat = materialOf(i, k);
            if (flip) {
                b.tri(a, d, c, [uv[0], uv[3], uv[2]], mat);
                b.tri(a, c, bb, [uv[0], uv[2], uv[1]], mat);
            } else {
                b.tri(a, bb, c, [uv[0], uv[1], uv[2]], mat);
                b.tri(a, c, d, [uv[0], uv[2], uv[3]], mat);
            }
        }
    }
}

/** How far below the cloth's edge the pocket walls lean back (under the cloth, behind the liners), so they never
 * share a surface with the liners at the backs of the pockets (they used to, and flickered as a saw-tooth rim). */
const POCKET_WALL_LEAN = 0.008;

// Cushion cross-sections, [offset outwards from the nose line, height], same point count for both so they blend.
const CLOTH_SECTION: [number, number][] = [
    [0.16, BED_Y], [0.115, BED_Y + 0.1], [0.05, BED_Y + 0.22], [0.012, NOSE_Y - 0.05], [0, NOSE_Y],
    [0.004, NOSE_Y + 0.025], [0.018, NOSE_Y + 0.045], [0.04, CUSHION_TOP_Y - 0.006], [0.07, CUSHION_TOP_Y], [NOSE_WIDTH, CUSHION_TOP_Y],
];
// the pocket liner: drops straight into the pocket from the same rounded top
const LINER_SECTION: [number, number][] = [
    [0, BED_Y - POCKET_DEPTH], [0, BED_Y - 0.25], [0, BED_Y - 0.08], [0.0, NOSE_Y - 0.12], [0, NOSE_Y],
    [0.004, NOSE_Y + 0.025], [0.018, NOSE_Y + 0.045], [0.04, CUSHION_TOP_Y - 0.006], [0.07, CUSHION_TOP_Y], [NOSE_WIDTH, CUSHION_TOP_Y],
];

const TableModel = {
    /** Builds the table; add the returned object to the scene. */
    build(profile: QualityProfile = QUALITY_PROFILES.high) {
        const root = new THREE.Object3D();
        root.name = "table";
        const legSegments = profile.legSegments;

        const clothMap = imageTexture("../textures/table/cloth.jpg", profile.textureScale);
        const woodMap = imageTexture("../textures/table/wood.jpg", profile.textureScale);
        const leatherMap = imageTexture("../textures/table/leather.jpg", profile.textureScale);

        const cloth = new THREE.MeshPhongMaterial({map: clothMap, color: 0xffffff, ambient: 0x8a8a8a, specular: 0x0c0c0c, shininess: 4});
        const cushionCloth = new THREE.MeshPhongMaterial({map: clothMap, color: 0xe6e6e6, ambient: 0x7a7a7a, specular: 0x101010, shininess: 6});
        const leather = new THREE.MeshPhongMaterial({map: leatherMap, color: 0xffffff, ambient: 0x8a8a8a, specular: 0x2a2018, shininess: 22, side: THREE.DoubleSide});
        const pocketDark = new THREE.MeshLambertMaterial({color: 0x050404, ambient: 0x030303, side: THREE.DoubleSide});
        const rimCloth = new THREE.MeshLambertMaterial({map: clothMap, color: 0x9a9a9a, ambient: 0x555555, side: THREE.DoubleSide});
        const wood = new THREE.MeshPhongMaterial({map: woodMap, color: 0xffffff, ambient: 0x909090, specular: 0x2c2c2c, shininess: 40});
        const frameWood = new THREE.MeshPhongMaterial({map: woodMap, color: 0x9a7466, ambient: 0x6a5048, specular: 0x1c1c1c, shininess: 30});
        const marking = new THREE.MeshLambertMaterial({color: 0xf4f4ec, ambient: 0xb8b8b0, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2});
        const diamond = new THREE.MeshPhongMaterial({color: 0xf6eedb, ambient: 0xc8c0b0, specular: 0x666666, shininess: 70, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2});

        // the cushion line as drawn: smoothed on its curved parts on High (every physics point kept)
        // (densified the same for the liners and the pocket openings, so they share their points)
        const cushion = densify(smoothCushionPath(profile.curveSubdivisions), 0.25);
        const pockets = POCKETS.map((_, i) => pocketHole(i, cushion));
        const holes = pockets.map(p => p.outline);
        const railInner = railInnerEdge();
        const clothUV = (p: Vec): UV => [p[0] / CLOTH_TILE, p[1] / CLOTH_TILE];

        // ---- cloth bed with the pocket openings, the pocket walls and bottoms (one mesh: cloth, dark, rim) ----
        {
            const b = new MeshBuilder();
            addFlat(b, railInner, holes, BED_Y, clothUV, () => 0);
            pockets.forEach(({outline: hole, centre}) => {
                const n = hole.length;
                const base = b.geometry.vertices.length;
                // [height, lean outwards from the opening's centre]: the cloth's edge, then back under it
                const rows: [number, number][] = [[BED_Y, 0], [BED_Y - 0.035, POCKET_WALL_LEAN], [BED_Y - POCKET_DEPTH, POCKET_WALL_LEAN]];
                for (const p of hole) {
                    const d = Math.hypot(p[0] - centre[0], p[1] - centre[1]) || 1;
                    const ox = (p[0] - centre[0]) / d, oy = (p[1] - centre[1]) / d;
                    for (const [h, lean] of rows) b.vertex(p[0] + ox * lean, h, p[1] + oy * lean);
                }
                for (let i = 0; i < n; i++) {
                    const j = (i + 1) % n;
                    for (let r = 0; r < rows.length - 1; r++) {
                        const a = base + i * 3 + r, bb = base + j * 3 + r, c = base + j * 3 + r + 1, d = base + i * 3 + r + 1;
                        const uv: UV = [0, 0];
                        // outline is counter-clockwise: faces wound this way look into the pocket
                        b.tri(a, c, bb, [uv, uv, uv], r === 0 ? 2 : 1);
                        b.tri(a, d, c, [uv, uv, uv], r === 0 ? 2 : 1);
                    }
                }
                const bottom = hole.map(p => {
                    const d = Math.hypot(p[0] - centre[0], p[1] - centre[1]) || 1;
                    return [p[0] + (p[0] - centre[0]) / d * POCKET_WALL_LEAN, p[1] + (p[1] - centre[1]) / d * POCKET_WALL_LEAN] as Vec;
                });
                addFlat(b, bottom, [], BED_Y - POCKET_DEPTH, () => [0, 0], () => 1);
            });
            // smooth normals: the cloth is flat anyway, and the rim of each opening is lit as one rounded band
            const mesh = new THREE.Mesh(b.build(true), new THREE.MeshFaceMaterial([cloth, pocketDark, rimCloth]));
            mesh.receiveShadow = true;
            mesh.castShadow = false;
            root.add(mesh);
        }

        // ---- cushions and pocket liners (rounded nose along the cushion line) and the flat cushion tops ----
        {
            const path = cushion;
            const mitres = outwardMitres(path);
            const w = path.map(linerWeight);
            const b = new MeshBuilder();
            addSweep(b, path, mitres, i => CLOTH_SECTION.map((c, k) => [
                c[0] + (LINER_SECTION[k][0] - c[0]) * w[i], c[1] + (LINER_SECTION[k][1] - c[1]) * w[i]] as [number, number]),
                -1, [CLOTH_TILE, CLOTH_TILE], (i, k) => {
                    const leatherHere = (w[i] + w[(i + 1) % path.length]) / 2 > 0.5;
                    if (!leatherHere) return 0;
                    return k < 2 ? 2 : 1;   // the liner's part inside the pocket, below the cloth, is in the dark
                });
            const noseEnd = path.map((p, i) => [p[0] + mitres[i][0] * NOSE_WIDTH, p[1] + mitres[i][1] * NOSE_WIDTH] as Vec);
            addFlat(b, railInner, [noseEnd], CUSHION_TOP_Y, clothUV, c => linerWeight(c) > 0.5 ? 1 : 0);
            const mesh = new THREE.Mesh(b.build(true), new THREE.MeshFaceMaterial([cushionCloth, leather, pocketDark]));
            mesh.receiveShadow = true;
            mesh.castShadow = false;
            root.add(mesh);
        }

        // ---- rails: top (grain along each rail, mitred), inner lip, rounded outer edge and the frame below ----
        {
            const b = new MeshBuilder();
            const outerTop = railOuterEdge(0.06, profile.railCornerSegments);
            const outer = railOuterEdge(0, profile.railCornerSegments);
            const ox0 = TABLE.xMin, ox1 = TABLE.xMax, oy0 = TABLE.yMin, oy1 = TABLE.yMax;
            const alongY = (c: Vec) => {
                // long rails (the x sides) carry the grain along y; the short ones along x; the corners split on the diagonal
                const dx = Math.min(Math.abs(c[0] - ox0), Math.abs(c[0] - ox1));
                const dy = Math.min(Math.abs(c[1] - oy0), Math.abs(c[1] - oy1));
                const insideX = c[0] > ox0 && c[0] < ox1, insideY = c[1] > oy0 && c[1] < oy1;
                if (insideY && !insideX) return true;
                if (insideX && !insideY) return false;
                return dx < dy;
            };
            const woodUV = (p: Vec, c: Vec): UV => alongY(c) ? [p[1] / WOOD_TILE_U, p[0] / WOOD_TILE_V] : [p[0] / WOOD_TILE_U, p[1] / WOOD_TILE_V];
            addFlat(b, outerTop, [railInner], RAIL_TOP_Y, woodUV, () => 0);
            // inner lip: from just under the cushion top up to the rail top, facing the table
            const lipPath = railInner;
            addSweep(b, lipPath, lipPath.map(() => [0, 0] as Vec), () => [[0, CUSHION_TOP_Y - 0.06], [0, RAIL_TOP_Y]],
                -1, [WOOD_TILE_U, WOOD_TILE_V], () => 0);
            // outer edge: rounded top edge, the rail's side, a small step and the frame (apron), then its underside
            const mitres = outwardMitres(outer);
            addSweep(b, outer, mitres, () => [
                [-0.06, RAIL_TOP_Y], [-0.032, RAIL_TOP_Y - 0.008], [-0.012, RAIL_TOP_Y - 0.028], [-0.002, RAIL_TOP_Y - 0.05], [0, RAIL_TOP_Y - 0.07],
                [0, RAIL_BOTTOM_Y], [-0.05, RAIL_BOTTOM_Y - 0.02], [-0.07, RAIL_BOTTOM_Y - 0.06], [-0.07, FRAME_BOTTOM_Y + 0.05],
                [-0.09, FRAME_BOTTOM_Y], [-0.75, FRAME_BOTTOM_Y],
            ], 1, [WOOD_TILE_U, WOOD_TILE_V], (i, k) => k >= 6 ? 1 : 0);
            // underside of the table, closing the frame
            const under = outer.map((p, i) => [p[0] + mitres[i][0] * -0.75, p[1] + mitres[i][1] * -0.75] as Vec);
            const ub = new MeshBuilder();
            addFlat(ub, under, [], FRAME_BOTTOM_Y, p => [p[0] / WOOD_TILE_U, p[1] / WOOD_TILE_V], () => 0);
            for (const f of ub.geometry.faces) { const t = f.b; f.b = f.c; f.c = t; }   // face down
            for (const uv of ub.geometry.faceVertexUvs[0]) { const t = uv[1]; uv[1] = uv[2]; uv[2] = t; }
            const mesh = new THREE.Mesh(b.build(true), new THREE.MeshFaceMaterial([wood, frameWood]));
            mesh.receiveShadow = true;
            root.add(mesh);
            root.add(new THREE.Mesh(ub.build(false), frameWood));

            // diamonds, inlaid flush on the rail top
            const db = new MeshBuilder();
            for (const d of diamondPositions()) {
                const [x, y] = d.p;
                const along = 0.11, across = 0.045;
                const pts: Vec[] = d.alongY ? [[x, y - along], [x + across, y], [x, y + along], [x - across, y]]
                    : [[x - along, y], [x, y + across], [x + along, y], [x, y - across]];
                const ids = pts.map(p => db.vertex(p[0], RAIL_TOP_Y + 0.001, p[1]));
                db.triUp(ids[0], ids[1], ids[2], [[0, 0], [0, 0], [0, 0]], 0);
                db.triUp(ids[0], ids[2], ids[3], [[0, 0], [0, 0], [0, 0]], 0);
            }
            root.add(new THREE.Mesh(db.build(false), diamond));
        }

        // ---- markings: baulk line, the D (baulk side) and the spots ----
        {
            const b = new MeshBuilder();
            const h = BED_Y + 0.002;
            const lw = 0.024;   // line width
            const quad = (p: Vec[]) => {
                const ids = p.map(q => b.vertex(q[0], h, q[1]));
                b.triUp(ids[0], ids[1], ids[2], [[0, 0], [0, 0], [0, 0]], 0);
                b.triUp(ids[0], ids[2], ids[3], [[0, 0], [0, 0], [0, 0]], 0);
            };
            quad([[TABLE.xMin, BAULK_Y - lw / 2], [TABLE.xMax, BAULK_Y - lw / 2], [TABLE.xMax, BAULK_Y + lw / 2], [TABLE.xMin, BAULK_Y + lw / 2]]);
            const seg = 64;
            for (let i = 0; i < seg; i++) {
                // half circle towards the baulk cushion (negative y), from +x round to -x
                const a0 = -Math.PI * i / seg, a1 = -Math.PI * (i + 1) / seg;
                const r0 = D_RADIUS - lw / 2, r1 = D_RADIUS + lw / 2;
                const c = D_CENTRE;
                quad([[c[0] + r0 * Math.cos(a0), c[1] + r0 * Math.sin(a0)], [c[0] + r1 * Math.cos(a0), c[1] + r1 * Math.sin(a0)],
                    [c[0] + r1 * Math.cos(a1), c[1] + r1 * Math.sin(a1)], [c[0] + r0 * Math.cos(a1), c[1] + r0 * Math.sin(a1)]]);
            }
            for (const id of Object.keys(SPOTS) as (keyof typeof SPOTS)[]) {
                const [x, y] = SPOTS[id];
                const r = 0.032, n = 20;
                const centre = b.vertex(x, h + 0.0005, y);
                const ring = Array.from({length: n}, (_, k) => b.vertex(x + r * Math.cos(2 * Math.PI * k / n), h + 0.0005, y + r * Math.sin(2 * Math.PI * k / n)));
                for (let k = 0; k < n; k++) b.triUp(centre, ring[k], ring[(k + 1) % n], [[0, 0], [0, 0], [0, 0]], 0);
            }
            const mesh = new THREE.Mesh(b.build(false), marking);
            mesh.receiveShadow = true;
            root.add(mesh);
        }

        // ---- legs: six turned legs with a square block under the frame ----
        {
            const b = new MeshBuilder();
            const profile: [number, number][] = [   // [radius, height]: bun foot, long taper, collars under the block
                [0.0, 0.0], [0.3, 0.0], [0.34, 0.08], [0.3, 0.2], [0.25, 0.3], [0.24, 0.65], [0.28, 0.8], [0.23, 0.95],
                [0.21, 2.6], [0.24, 4.3], [0.29, 4.85], [0.31, 5.0], [0.25, 5.15], [0.28, 5.42], [0.34, 5.58], [0.34, 5.75], [0.0, 5.75],
            ];
            const seg = legSegments;
            const xs = [TABLE.xMin - 0.6, TABLE.xMax + 0.6], ys = [TABLE.yMin - 0.5, TABLE_CENTRE[1], TABLE.yMax + 0.5];
            for (const lx of xs) {
                for (const ly of ys) {
                    const base = b.geometry.vertices.length;
                    for (let s = 0; s <= seg; s++) {
                        const a = 2 * Math.PI * s / seg;
                        for (const [r, h] of profile) b.vertex(lx + r * Math.cos(a), h, ly + r * Math.sin(a));
                    }
                    const m = profile.length;
                    for (let s = 0; s < seg; s++) {
                        for (let k = 0; k < m - 1; k++) {
                            const a = base + s * m + k, bb = base + (s + 1) * m + k, c = base + (s + 1) * m + k + 1, d = base + s * m + k + 1;
                            const uv: [UV, UV, UV, UV] = [[s / seg, profile[k][1] / 3], [(s + 1) / seg, profile[k][1] / 3], [(s + 1) / seg, profile[k + 1][1] / 3], [s / seg, profile[k + 1][1] / 3]];
                            b.tri(a, d, c, [uv[0], uv[3], uv[2]], 0);
                            b.tri(a, c, bb, [uv[0], uv[2], uv[1]], 0);
                        }
                    }
                }
            }
            const legs = new THREE.Mesh(b.build(true), frameWood);
            root.add(legs);
            // the square blocks between the legs and the frame, merged into one mesh (one draw call, not six)
            const blockH = FRAME_BOTTOM_Y - 5.75;
            const blocks = new THREE.Geometry();
            for (const lx of xs) {
                for (const ly of ys) {
                    const block = new THREE.Mesh(new THREE.CubeGeometry(0.72, blockH, 0.72), frameWood);
                    block.position.set(lx, 5.75 + blockH / 2, ly);
                    block.updateMatrix();
                    THREE.GeometryUtils.merge(blocks, block);
                }
            }
            if (blocks.computeCentroids) blocks.computeCentroids();
            blocks.computeFaceNormals();
            root.add(new THREE.Mesh(blocks, frameWood));
        }

        return root;
    },
};

export default TableModel;
